import React from 'react';

import {
    IconArrowDown,
    IconArrowRight
} from '$partials/icon/index.js';

import {
    formatLinkBlockToUrl,
    formatStudioCapacity
} from '$utilities/format.js';

import { CtaList, Lightbox } from '$partials/modules/blocks/index.js';
import { Disclosure } from '$partials/ui/index.js';
import { Image } from '$partials/media/index.js';
import { isLocalURL } from '$utilities/browser';
import { ModuleDispatcher } from '$partials/modules/index.js';
import { PageShell } from '$partials/global/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').StudioDetailPage}
 */
const StudioDetailPage = (props) => {
    const page = props.data?.page;

    const capacity = formatStudioCapacity(page);
    const content = page?.content || [];
    const ctaHref = formatLinkBlockToUrl(page?.ctaLink, page.title);
    const isCtaExternal = ctaHref && !isLocalURL(ctaHref);
    const facilities = page?.facilities?.filter(Boolean) || [];
    const accessibleFacilities = page?.accessibility?.filter(Boolean) || [];
    const gallery = page?.gallery || [];
    const packHref = formatLinkBlockToUrl(page?.packLink);
    const isPackExternal = packHref && !isLocalURL(packHref);

    const otherStudios = page?.siblings?.edges
        ?.map((edge) => edge.node)
        .filter((studio) => studio?.id) || [];

    const otherStudioRows = otherStudios.map((studio) => ({
        description: formatStudioCapacity(studio),
        id: studio.id,
        label: studio.title,
        link: {
            linkTo: 'page',
            page: { url: studio.url }
        }
    }));

    return (
        <PageShell
            banner
            footer={props.data?.footer}
            header={props.data?.header}>
            <div className='layout-container bg-neutral-00'>
                <div className='py-16 sm:py-40'>
                    <div className='layout-grid flex-col sm:flex-row gap-y-38 sm:gap-y-0'>
                        {/* Page header */}
                        <div className='w-full sm:w-4/12 md:w-3/12 layout-grid-col'>
                            <h1 className='type-style-heading'>{page?.title}</h1>

                            {page?.introduction && (
                                <div
                                    className='rich-text type-style-heading'
                                    dangerouslySetInnerHTML={{ __html: page.introduction }} />
                            )}

                            {(page?.ctaLabel && ctaHref) && (
                                <p className='fixed bottom-0 z-10 w-full -mx-8 sm:-mx-24 md:mx-0 md:w-auto md:relative md:mt-32'>
                                    <a
                                        className='button-overlay opacity-90 md:opacity-100 w-full md:w-auto md:button type-style-body cursor-pointer'
                                        href={ctaHref}
                                        rel={isCtaExternal ? 'noopener noreferrer' : undefined}
                                        target={isCtaExternal ? '_blank' : undefined}>
                                        {page.ctaLabel}

                                        <span
                                            aria-hidden={true}
                                            className='ml-5'>
                                            <IconArrowRight
                                                height={20}
                                                width={20} />
                                        </span>
                                    </a>
                                </p>
                            )}

                            {packHref && (
                                <p className={`${page?.ctaLabel && ctaHref ? 'mt-24 sm:mt-32 md:mt-8' : 'mt-24 sm:mt-32'}`}>
                                    <a
                                        className='button type-style-body cursor-pointer'
                                        href={packHref}
                                        rel={isPackExternal ? 'noopener noreferrer' : undefined}
                                        target={isPackExternal ? '_blank' : undefined}>
                                        Download Producer Pack

                                        <span
                                            aria-hidden={true}
                                            className='ml-5'>
                                            <IconArrowDown
                                                height={20}
                                                width={20}/>
                                        </span>
                                    </a>
                                </p>
                            )}
                        </div>

                        {/* The specification a producer assesses the studio by */}
                        <div className='w-full sm:w-8/12 md:w-9/12 layout-grid-col'>
                            {capacity && (
                                <Disclosure
                                    heading='Capacity'
                                    isOpenByDefault={true}>
                                    <p className='mt-4 type-style-heading pb-8'>
                                        {capacity}
                                    </p>
                                </Disclosure>
                            )}

                            {facilities.length > 0 && (
                                <Disclosure heading='Facilities'>
                                    <ul className='pb-10 sm:pb-20 columns-2'>
                                        {facilities.map((facility, index) => (
                                            <li
                                                className='type-style-body sm:type-style-heading'
                                                key={index}>
                                                {facility}
                                            </li>
                                        ))}
                                    </ul>
                                </Disclosure>
                            )}

                            {accessibleFacilities.length > 0 && (
                                <Disclosure heading='Accessibility'>
                                    <ul className='pb-10 sm:pb-20 columns-2'>
                                        {accessibleFacilities.map((accessibility, index) => (
                                            <li
                                                className='type-style-body sm:type-style-heading'
                                                key={index}>
                                                {accessibility}
                                            </li>
                                        ))}
                                    </ul>
                                </Disclosure>
                            )}

                            {page?.floorplan && (
                                <Disclosure heading='View Floorplan'>
                                    <figure className='pb-10 sm:pb-20 text-[0] leading-[0] tracking-normal pointer-events-none select-none'>
                                        <Image {...page.floorplan} />
                                    </figure>
                                </Disclosure>
                            )}

                        </div>
                    </div>
                </div>
            </div>

            {gallery.length > 0 && (
                <Lightbox
                    items={gallery}
                    modulePaddingClasses='pb-16 sm:pb-40'
                    offsetColumns={false} />
            )}

            {otherStudioRows.length > 0 && (
                <CtaList
                    label='Other Studios'
                    modulePaddingClasses='pb-16 sm:pb-40'
                    rows={otherStudioRows} />
            )}

            {/* Streamfield modules */}
            {content.length > 0 && (
                <div>
                    {content.map((module) => (
                        <ModuleDispatcher
                            key={module.id}
                            modulePaddingClasses='pb-24 sm:pb-32'
                            { ...module } />
                    ))}
                </div>
            )}
        </PageShell>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default StudioDetailPage;
